import { useEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/** Treat the bar as docked within a pixel of the viewport top. */
const DOCK_EPSILON = 1;

/**
 * Navigation state a link sets to ask for the docked landing without being one
 * of the tab pills.
 *
 * The docked test reads the tab bar's position on a click the bar itself hears,
 * so a link in the body of a tab — "View all Products" under the featured grid
 * — can never arm it, and the reader is thrown back to the top of the page.
 * Such a link sits below the fold by construction, which is the same situation
 * a docked bar describes.
 */
export const LAND_ON_TAB_CONTENT = 'landOnTabContent';

/** How long to keep correcting the landing, in frames (~0.5s at 60fps). */
const SETTLE_FRAMES = 30;

/**
 * Where the reader lands when switching profile tabs.
 *
 * Two cases, decided by whether the tab bar is docked — locked to the top of
 * the viewport — at the moment the tab is clicked:
 *
 * - **Docked.** The reader is down in the body of a tab, and the bar is all
 *   they can see of the page's chrome. The new tab opens at the top of ITS
 *   content, sitting directly under the bar. Keeping their old offset would
 *   drop them into the middle of content they have not seen.
 * - **Not docked.** They are further up, with the banner and profile header in
 *   view. Nothing moves — a tab click there should not throw the page around.
 *
 * The landing is reapplied for half a second: the incoming tab lays out before
 * its images have sized and its scroll-reveal sections have run, so a single
 * scroll can be undone by the browser clamping to a document that is briefly
 * shorter. It gives way the moment the reader takes over — any wheel, touch or
 * key ends it, so it can never fight someone scrolling.
 *
 * Skips the initial mount, same-tab re-renders (query/filter changes),
 * cross-professional navigations (a fresh landing, not a tab switch), and
 * POP navigations (Back restores the browser's own saved position).
 */
export const useTabScrollAnchor = (
    navRef: RefObject<HTMLElement | null>,
): void => {
    const { pathname, state } = useLocation();
    const navigationType = useNavigationType();
    const askedForTabContent = Boolean(
        (state as Record<string, unknown> | null)?.[LAND_ON_TAB_CONTENT],
    );
    const profileBase = pathname.split('/').slice(0, 3).join('/');
    const tabKey = pathname.replace(/\/+$/, '');
    const previousTabRef = useRef({ profileBase, tabKey });
    // Read on the click: by the time the navigation's effect runs the browser
    // may already have clamped the scroll, undocking the bar and losing the
    // very fact we need.
    const wasDockedRef = useRef(false);

    useEffect(() => {
        const nav = navRef.current;
        if (!nav) return;
        const capture = () => {
            wasDockedRef.current =
                nav.getBoundingClientRect().top <= DOCK_EPSILON;
        };
        // Capture phase: the link's own handler navigates, so a bubbling
        // listener would read the position after the fact.
        nav.addEventListener('click', capture, true);
        return () => nav.removeEventListener('click', capture, true);
    }, [navRef]);

    useEffect(() => {
        const previous = previousTabRef.current;
        previousTabRef.current = { profileBase, tabKey };
        const wasDocked = wasDockedRef.current;
        wasDockedRef.current = false;

        if (previous.tabKey === tabKey) return;
        if (previous.profileBase !== profileBase) return;
        if (navigationType === 'POP') return;
        if (!wasDocked && !askedForTabContent) return;

        const nav = navRef.current;
        if (!nav) return;

        let frame = 0;
        let frames = 0;
        const stop = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = 0;
            window.removeEventListener('wheel', stop);
            window.removeEventListener('touchstart', stop);
            window.removeEventListener('keydown', stop);
            window.removeEventListener('pointerdown', stop);
        };
        const land = () => {
            const body = document.getElementById('main-content');
            if (body) {
                // The bar's own height is the offset: scrolling the tab body's
                // top edge that far down puts it flush under the docked bar.
                const bodyTop =
                    window.scrollY + body.getBoundingClientRect().top;
                const target = Math.max(
                    0,
                    bodyTop - nav.getBoundingClientRect().height,
                );
                if (Math.round(window.scrollY) !== Math.round(target)) {
                    window.scrollTo(0, target);
                }
            }
            if (++frames >= SETTLE_FRAMES) return stop();
            frame = requestAnimationFrame(land);
        };

        window.addEventListener('wheel', stop, { passive: true, once: true });
        window.addEventListener('touchstart', stop, {
            passive: true,
            once: true,
        });
        window.addEventListener('keydown', stop, { once: true });
        // Dragging the scrollbar raises neither wheel nor touch, so without
        // this the correction would fight the drag for the rest of its half
        // second — the one input where it is most visible.
        window.addEventListener('pointerdown', stop, {
            passive: true,
            once: true,
        });
        land();
        return stop;
        // navRef is a stable ref object; only navigation identity re-runs this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabKey, profileBase, navigationType, askedForTabContent]);
};
